import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Camera, FolderKanban, ChevronRight, Smartphone, Loader2 } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono } from "../ui";

export default function Events() {
  const { store, version, syncing } = useField();
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [cl, assets, photos] = await Promise.all([store.getClaims(), store.all("assets"), store.all("photos")]);
      setClaims(cl);
      const c = {};
      cl.forEach((x) => (c[x.id] = { assets: 0, photos: 0 }));
      assets.forEach((a) => c[a.claim_id] && c[a.claim_id].assets++);
      photos.forEach((p) => c[p.claim_id] && c[p.claim_id].photos++);
      setCounts(c);
    })();
  }, [store, version]);

  return (
    <div className="space-y-5" data-testid="field-events">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">Field capture</Mono>
        <h1 className="font-heading text-2xl mt-1">Disaster Events</h1>
        <p className="text-white/50 text-sm mt-1 font-light">Tap an event to capture GPS-tagged damage evidence.</p>
      </div>

      <button
        onClick={() => nav("/field/connect")}
        data-testid="field-connect-link"
        className="w-full flex items-center justify-between border border-gold-500/25 bg-navy-900/50 px-4 py-3 rounded-sm"
      >
        <span className="flex items-center gap-2 text-sm text-gold-300"><Smartphone className="h-4 w-4" /> Connect to a team</span>
        <ChevronRight className="h-4 w-4 text-white/40" />
      </button>

      {claims.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 rounded-sm p-10 text-center text-white/45 text-sm" data-testid="field-events-empty">
          {syncing ? (
            <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading events…</span>
          ) : (
            <>No events yet. Connect to your team with the 6-character code, or ask your lead assessor to create an event in The MDAu Office.</>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map((c) => (
            <button
              key={c.id}
              onClick={() => nav(`/field/event/${c.id}`)}
              data-testid={`field-event-${c.id}`}
              className="w-full text-left border border-gold-500/25 bg-navy-900/50 rounded-sm p-4 active:bg-navy-800"
            >
              <div className="flex items-center justify-between">
                <Mono className="text-[0.55rem] text-gold-500/70">{c.assessing_body || c.region || "NSW"}</Mono>
                {c.latitude != null && c.longitude != null && <MapPin className="h-3.5 w-3.5 text-emerald-400" />}
              </div>
              <h3 className="font-heading text-lg mt-1 leading-snug">{c.name}</h3>
              {c.council && <p className="text-white/50 text-xs mt-0.5">{c.council}</p>}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gold-500/15 text-xs text-white/60">
                <span className="flex items-center gap-1.5"><FolderKanban className="h-3.5 w-3.5 text-gold-500/60" /> {counts[c.id]?.assets || 0} assets</span>
                <span className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5 text-gold-500/60" /> {counts[c.id]?.photos || 0} photos</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
